import { format } from 'date-fns';
import { Timestamp } from 'firebase/firestore';
import { track } from '../tracking';

export function dateFormat(date: Date, formatStr: string): string {
    const formatMap: Record<string, string> = {
        'h:MM TT': 'h:mm a',
        'dS mmm': 'do MMM',
    };

    const actualFormat = formatMap[formatStr] || formatStr;
    return format(date, actualFormat);
}

export function utcToIndianTZ(date: Date | string): Date {
    const dateUTC = new Date(date);
    const dateIST = new Date(dateUTC.getTime());
    const currentTimezoneOffset = dateUTC.getTimezoneOffset();
    //date shifting for IST timezone (+5 hours and 30 minutes)
    // dateIST.setHours(dateIST.getHours() + 5);
    dateIST.setMinutes(dateIST.getMinutes() + currentTimezoneOffset + 330);
    return dateIST;
}

const colors = {
    darwin: {
        neutral: {
            0: '#FFFFFF',
            50: '#F4F4F4',
            100: '#E8E8E8',
            200: '#D1D1D1',
            300: '#BABABA',
            400: '#A3A3A3',
            600: '#767676',
            800: '#484848',
            1000: '#1A1A1A',
        },
        primary: {
            'bg-light': '#E9EFFF',
            'bg-dark': '#D3DFFF',
            light: '#7A9FFF',
            primary: '#215FFF',
            dark: '#143999',
        },
        green: {
            'bg-light': '#E8F6F0',
            'bg-dark': '#D1EDE1',
            light: '#75CAA6',
            primary: '#19A66A',
            dark: '#0F6440',
        },
        yellow: {
            'bg-light': '#FDF7E7',
            'bg-dark': '#FBEFCE',
            light: '#F8DF9E',
            primary: '#EDAE0C',
            dark: '#8E6807',
        },
        red: {
            'bg-light': '#FBEDEC',
            'bg-dark': '#F7DBD9',
            light: '#E7938D',
            primary: '#D74B41',
            dark: '#821D27',
        },
        gradient: {
            gold: {
                'atom-1': '#CCA557',
                'atom-2': '#FFE5AE',
            },
            black: {
                'atom-1': '#373737',
                'atom-2': '#1A1A1A',
            },
            border: {
                black: {
                    'atom-1': '#4C4C4C',
                    'atom-2': '#2B2B2B',
                },
                blue: { 'atom-1': '#B0C6FF', 'atom-2': '#E9EFFF' },
            },
        },
    },
};

type TActivityIcon = {
    iconType: 'far' | 'fas';
    iconName:
        | 'tag'
        | 'circle-check'
        | 'calendar'
        | 'archive'
        | 'circle-exclamation'
        | 'person-walking-dashed-line-arrow-right'
        | 'stethoscope'
        | 'repeat'
        | 'circle-xmark'
        | 'message-pen';
};

export type FirestoreAppointmentActivityEvent = {
    event: string;
    actor_name: string;
    actor_role: 'DOCTOR' | 'STAFF';
    created_at: Timestamp;
};

type TActivityLogStyles = {
    title: string;
    actor: string;
    actorName: string;
    titleStyle: string;
    titleFont: string;
    actorFont: string;
    actorNameFont: string;
    actionIcon: TActivityIcon;
    dayByMonth: string;
    time: string;
    actor_role: string;
};

export function getActivityLogStylesClient(
    activity: FirestoreAppointmentActivityEvent,
): TActivityLogStyles | null {
    try {
        const aptActivityAction = activity.event;
        const actor = activity.actor_name?.toLowerCase() || 'system';
        const date = new Date(activity.created_at?.toDate?.() || activity.created_at);
        const isDoctor = activity.actor_role === 'DOCTOR';

        let title = '';
        let titleStyle = '';
        const titleFont = 'SubheadlineBold';
        const actorFont = 'CalloutRegular';
        const actorNameFont = 'CalloutBold';
        const actionIcon: TActivityIcon = {
            iconType: 'far',
            iconName: 'tag',
        };
        const dateInIst = utcToIndianTZ(date);
        const dayByMonth = dateFormat(dateInIst, 'dS mmm');
        const time = dateFormat(dateInIst, 'h:MM TT');

        switch (aptActivityAction) {
            case 'APT_MUTLI_TAG':
                title = 'Multi tag';
                titleStyle = colors.darwin.primary.primary;
                actionIcon.iconName = 'tag';
                break;
            case 'APT_PAYMENT_UPDATED':
                title = 'Payment updated';
                titleStyle = colors.darwin.green.primary;
                actionIcon.iconName = 'circle-check';
                break;
            case 'APT_REMINDER_SENT':
                title = 'Reminder Sent';
                titleStyle = colors.darwin.primary.primary;
                actionIcon.iconName = 'calendar';
                break;
            case 'VISIT_ARCHIVED':
                title = 'Visit archived';
                titleStyle = colors.darwin.neutral[600];
                actionIcon.iconName = 'archive';
                break;
            case 'VISIT_RESCHEDULED':
                title = 'Visit rescheduled';
                titleStyle = colors.darwin.yellow.dark;
                actionIcon.iconName = 'calendar';
                break;
            case 'APT_TAG_ADDED':
                title = 'Tag added';
                titleStyle = colors.darwin.primary.primary;
                actionIcon.iconName = 'tag';
                break;
            case 'PREPAY_FAILED':
                title = 'Prepay failed';
                titleStyle = colors.darwin.red.primary;
                actionIcon.iconName = 'circle-exclamation';
                break;
            case 'APT_TAG_DELETED':
                title = 'Tag deleted';
                titleStyle = colors.darwin.red.primary;
                actionIcon.iconName = 'tag';
                break;
            case 'VISIT_CHECKED_IN':
                title = 'Visit checked in';
                titleStyle = colors.darwin.green.primary;
                actionIcon.iconName = 'person-walking-dashed-line-arrow-right';
                break;
            case 'FOLLOWUP_REMINDER_SENT':
                title = 'Follow up reminder sent';
                titleStyle = colors.darwin.primary.primary;
                actionIcon.iconName = 'repeat';
                break;
            case 'VISIT_STARTED':
                title = 'Visit started';
                titleStyle = colors.darwin.green.primary;
                actionIcon.iconName = 'stethoscope';
                break;
            case 'VISIT_RESUMED':
                title = 'Visit Resumed';
                titleStyle = colors.darwin.green.primary;
                actionIcon.iconName = 'stethoscope';
                break;
            case 'VISIT_ENDED':
                title = 'Visit ended';
                titleStyle = colors.darwin.yellow.dark;
                actionIcon.iconName = 'circle-check';
                break;
            case 'MARKED_NOSHOW':
                title = 'Marked-no-show';
                titleStyle = colors.darwin.red.primary;
                actionIcon.iconName = 'circle-xmark';
                break;
            case 'RESCHEDULE_CREATED':
                title = 'Reschedule created';
                titleStyle = colors.darwin.primary.primary;
                actionIcon.iconName = 'calendar';
                break;
            case 'FOLLOWUP_CHECKED_IN':
                title = 'Follow up checked in';
                titleStyle = colors.darwin.green.primary;
                actionIcon.iconName = 'person-walking-dashed-line-arrow-right';
                break;
            case 'FOLLOWUP_DATE_ADDED':
                title = 'Follow up date added';
                titleStyle = colors.darwin.primary.primary;
                actionIcon.iconName = 'calendar';
                break;
            case 'FOLLOWUP_MESSAGE_UPDATE':
                title = 'Follow up message update';
                titleStyle = colors.darwin.primary.primary;
                actionIcon.iconName = 'message-pen';
                break;
            case 'CREATED':
                title = 'Appointment Created';
                titleStyle = colors.darwin.green.primary;
                actionIcon.iconName = 'circle-check';
                break;
            case 'RECEIPT_CREATED':
                title = 'Receipt Created';
                titleStyle = colors.darwin.green.primary;
                actionIcon.iconName = 'circle-check';
                break;
            case 'RECEIPT_DELETED':
                title = 'Receipt Deleted';
                titleStyle = colors.darwin.red.primary;
                actionIcon.iconName = 'circle-check';
                break;
            case 'RECEIPT_UPDATED':
                title = 'Receipt Updated';
                titleStyle = colors.darwin.primary.primary;
                actionIcon.iconName = 'circle-check';
                break;
            case 'ASSIGN_DOCTOR':
                title = 'Doctor Assigned';
                titleStyle = colors.darwin.green.primary;
                actionIcon.iconName = 'circle-check';
                break;
            default:
                title = aptActivityAction.replace(/_/g, ' ').toLowerCase();
                titleStyle = colors.darwin.neutral[1000];
                actionIcon.iconName = 'tag';
                break;
        }

        return {
            title,
            actor: `By ${actor}`,
            actorName:
                actor === 'system' ? 'Eka care' : `${isDoctor ? 'Dr.' : ''} ${activity.actor_name}`,
            titleStyle,
            titleFont,
            actorFont,
            actorNameFont,
            actionIcon,
            dayByMonth,
            time,
            actor_role: activity.actor_role.toLowerCase(),
        };
    } catch (error) {
        console.error('🔥 getActivityLogStylesClient error:', error);
        track('appointment_activity_style_paring_error', {
            data: JSON.stringify(activity),
            errorMessage: String(error),
        });
        return null;
    }
}
